// ============================================================================
// Hive Copilot — frontend chat module.
// A grounded, internal assistant: the team asks natural-language questions about
// the platform's own data (suppressions, delivery rate, campaigns, recent
// opt-outs) and can draft compliant SMS copy. Answers come from POST /api/copilot,
// which anchors every figure to real D1 data — so we surface a small
// "grounded in live data" caption under each answer.
//
// Uses only existing shared classes (card/pad, toolbar, mod-h/mod-sub, pill,
// btn/btn.ghost, h-input, msg) plus inline style="" for the chat bubbles.
// ============================================================================
import { api } from '../lib/api.js'
import { esc } from '../lib/view.js'

const SUGGESTIONS = [
  'Delivery rate?',
  'How many on the DNC list?',
  'Draft a compliant renewal offer text',
  'Recent opt-outs?',
]

export function renderCopilot(el) {
  el.innerHTML = `
    <div class="card pad">
      <div class="toolbar">
        <div class="grow">
          <div class="mod-h">Hive Copilot</div>
          <div class="mod-sub">Ask about the platform's own data — grounded in live D1 numbers, never invented. Also drafts compliant SMS copy.</div>
        </div>
      </div>

      <div id="cp-chips" style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:14px"></div>

      <div id="cp-log" style="display:flex;flex-direction:column;gap:10px;max-height:52vh;overflow-y:auto;padding:4px 2px 8px"></div>

      <div class="toolbar" style="margin:12px 0 0;align-items:flex-end">
        <textarea id="cp-input" class="h-input grow" rows="2"
          placeholder="Ask a question, or say “Draft a renewal offer text”…  (Enter to send · Shift+Enter for a new line)"></textarea>
        <button id="cp-send" class="btn" type="button">Ask</button>
      </div>
    </div>`

  const log = el.querySelector('#cp-log')
  const input = el.querySelector('#cp-input')
  const sendBtn = el.querySelector('#cp-send')
  const chips = el.querySelector('#cp-chips')

  // Client-side conversation history (kept short; server also caps it).
  const history = []
  let busy = false

  // Suggested-prompt chips — click fills + sends.
  chips.innerHTML = SUGGESTIONS
    .map((s, i) => `<button type="button" class="btn ghost" data-i="${i}" style="font-size:12px;padding:6px 12px">${esc(s)}</button>`)
    .join('')
  chips.querySelectorAll('button').forEach((b) => {
    b.addEventListener('click', () => {
      if (busy) return
      send(SUGGESTIONS[Number(b.dataset.i)])
    })
  })

  function bubble(role, contentHtml, grounded) {
    const isUser = role === 'user'
    const wrap = document.createElement('div')
    wrap.style.cssText = 'display:flex;justify-content:' + (isUser ? 'flex-end' : 'flex-start')
    const tint = isUser
      ? 'background:color-mix(in srgb, var(--turq) 16%, transparent);border:1px solid transparent'
      : 'background:var(--surface-2);border:1px solid var(--line)'
    const cap = (!isUser && grounded)
      ? '<div class="mod-sub" style="margin:6px 0 0;font-size:11px">' +
        '<span class="pill an">grounded in live data</span></div>'
      : ''
    wrap.innerHTML =
      '<div style="max-width:78%;padding:10px 13px;border-radius:14px;' + tint +
      ';font-size:13.5px;line-height:1.5;white-space:pre-wrap;word-break:break-word">' +
      contentHtml + cap + '</div>'
    log.appendChild(wrap)
    log.scrollTop = log.scrollHeight
    return wrap
  }

  async function send(text) {
    const message = (text != null ? text : input.value).trim()
    if (!message || busy) return
    busy = true
    sendBtn.disabled = true
    input.value = ''

    bubble('user', esc(message))
    history.push({ role: 'user', content: message })
    const thinking = bubble('assistant', '<span style="color:var(--muted)">Thinking…</span>')

    try {
      const res = await api.post('/api/copilot', { message, history: history.slice(-6) })
      const answer = (res && res.answer) || 'No answer returned.'
      thinking.remove()
      bubble('assistant', esc(answer), true)
      history.push({ role: 'assistant', content: answer })
    } catch (e) {
      thinking.remove()
      const err = bubble('assistant', '')
      const box = err.firstElementChild
      box.className = ''
      box.innerHTML = '<div class="msg bad" style="margin:0">Copilot error: ' + esc(e.message) + '</div>'
      box.style.cssText = 'max-width:78%;padding:10px 13px;border-radius:14px;background:var(--surface-2);border:1px solid var(--line)'
    } finally {
      busy = false
      sendBtn.disabled = false
      input.focus()
    }
  }

  sendBtn.addEventListener('click', () => send())
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  })

  // Opening greeting (assistant), no grounding caption.
  bubble('assistant', 'Hi — I\'m Hive Copilot. Ask me about suppressions, delivery rate, campaigns, or recent opt-outs, and I\'ll answer from live data. I can also draft a compliant SMS.')
  input.focus()
}
